import {
  Column,
  Container,
  Spacer,
  Text
} from '@smuchile/pkg-unimarc-components'
import classNames from 'classnames'
import { formatSubtotal } from '@smuchile/pkg-unimarc-hooks'
import {
  currencyFormat,
  getBemId,
  getGlobalStyle
} from '@smuchile/pkg-unimarc-components/helpers'
import { timeUTCFilter } from 'components/MyShipments/helpers/timeUTCFilter'
import { EXPRESS_SLA_ID, IAvailableWindowWithExpress } from '../../types'
import styles from '../../MyShipments.module.css'

const normalizeAmount = (amount?: string | number | null): number => {
  if (amount === undefined || amount === null || amount === '') {
    return Number.NaN
  }

  return typeof amount === 'number' ? amount : formatSubtotal(amount)
}

export interface SquareButtonProps {
  cartSubtotal?: string | number
  date: IAvailableWindowWithExpress
  endDate: string
  index?: number
  isMobile: boolean | string
  price: number
  scheduleSelected: IAvailableWindowWithExpress
  startDate: string
  handleSchedule: (date: IAvailableWindowWithExpress) => void
  onBlockedExpressClick?: (date: IAvailableWindowWithExpress) => void
}

export const SquareButton = ({
  cartSubtotal,
  date,
  endDate,
  index,
  isMobile,
  price,
  scheduleSelected,
  startDate,
  handleSchedule,
  onBlockedExpressClick
}: SquareButtonProps): React.ReactElement => {
  const startTime = new Date(startDate)
  const endTime = new Date(endDate)
  const formatedPrice = currencyFormat({
    number: price ?? 0,
    bool: false
  })
  const isExpress = date?.id === EXPRESS_SLA_ID
  const normalizedCartSubtotal = normalizeAmount(cartSubtotal)
  const normalizedExpressAmount = isExpress
    ? normalizeAmount(date?.expressAmount)
    : Number.NaN
  const isExpressDisabled =
    isExpress &&
    (Boolean(date?.expressExceed) ||
      !Number.isFinite(normalizedCartSubtotal) ||
      !Number.isFinite(normalizedExpressAmount) ||
      normalizedCartSubtotal > normalizedExpressAmount)
  const isSelected =
    !isExpressDisabled &&
    scheduleSelected?.startDateUtc === startDate &&
    scheduleSelected?.endDateUtc === endDate

  return (
    <Container
      border={`0.3px solid ${getGlobalStyle('--color-neutral-gray-divider')}`}
      borderRadius={getGlobalStyle('--border-radius-sm')}
      clickable={isExpressDisabled ? 'default' : 'pointer'}
      customClassName={classNames({
        [styles.optionSelected]: isSelected,
        [styles.expressOption]: isExpress,
        [styles.expressOptionDisabled]: isExpressDisabled
      })}
      id={getBemId('shipmentHour', 'hour', index?.toString())}
      maxHeight={isMobile ? '73px' : '80px'}
      maxWidth={isMobile ? '99px' : '140px'}
      onClick={() => {
        if (isExpressDisabled) return onBlockedExpressClick?.(date)
        return handleSchedule(date)
      }}
      position='relative'
    >
      {isExpress && (
        <span
          className={classNames(styles.expressBadge, {
            [styles.expressBadgeDisabled]: isExpressDisabled
          })}
        >
          Express ⚡
        </span>
      )}
      <Column
        alignItems='center'
        clickable={isExpressDisabled ? 'default' : 'pointer'}
        customHeight={isMobile ? '73px' : '80px'}
        customWidth={isMobile ? '99px' : '140px'}
        justifyContent='center'
        minHeight={isMobile ? '73px' : '80px'}
        minWidth={isMobile ? '99px' : '140px'}
      >
        <Text
          clickable={isExpressDisabled ? 'default' : 'pointer'}
          customClassName={classNames({
            [styles.expressTextDisabled]: isExpressDisabled
          })}
          fontSize={isMobile ? 'sm' : 'md'}
          fontWeight='semibold'
          id={getBemId('shipmentHour', 'hourText', index?.toString())}
        >
          {timeUTCFilter(startTime)} - {timeUTCFilter(endTime)}
        </Text>
        <Spacer.Horizontal size={8} />
        <Text
          clickable={isExpressDisabled ? 'default' : 'pointer'}
          customClassName={classNames({
            [styles.expressTextDisabled]: isExpressDisabled
          })}
          fontSize={isMobile ? 'sm' : 'md'}
          fontWeight='regular'
          id={getBemId('shipmentHour', 'price', index?.toString())}
        >
          {formatedPrice === '$0' ? 'Gratis' : formatedPrice}
        </Text>
      </Column>
    </Container>
  )
}
